import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ORDER_STATUS } from 'src/constants/common';
import { PATH } from 'src/constants/path';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { getTrackOrderDetail } from 'src/services/RadarService';
import { getDrivingRouteWithWaypoints } from 'src/utils/RouteUtils';
import WalToast from 'src/utils/WalToast';

import { getPhysicallyActiveLeg } from '../../Orders/OrderStatus/nba.helpers';
import { getStartedLegLocationUpdates } from '../track.helpers';
import {
    clearTrackOrderDetail,
    selectFullRouteDistance,
    selectRemainingDistance,
    selectSelectedLeg,
    selectSelectedLegIndex,
    setFullRouteDistance,
    setRemainingDistance,
    setSelectedLeg,
    setTrackDetailLoading,
    setTrackOrderDetail,
    trackDetailLoading,
    trackOrderDetail,
} from '../track.slice';

export const useRadarData = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const detailAbortRef = useRef<AbortController | null>(null);

    const trackOrderDetailData = useAppSelector(trackOrderDetail);
    const isDetailLoading = useAppSelector(trackDetailLoading);
    const selectedLeg = useAppSelector(selectSelectedLeg);
    // Mirrored into a ref so a silent re-fetch can read the CURRENT selection without
    // adding it to fetchOrderDetail's dependency list (that would rebuild the callback
    // on every leg change and re-trigger the effects that depend on its identity).
    const selectedLegRef = useRef<any>(selectedLeg);
    selectedLegRef.current = selectedLeg;
    const selectedLegIndex = useAppSelector(selectSelectedLegIndex);
    const fullRouteDistance = useAppSelector(selectFullRouteDistance);
    const remainingDistance = useAppSelector(selectRemainingDistance);

    const fetchOrderDetail = useCallback(
        (id: string, options?: { silent?: boolean }) => {
            const silent = options?.silent ?? false;

            if (detailAbortRef.current) detailAbortRef.current.abort();
            detailAbortRef.current = new AbortController();

            if (!silent) {
                dispatch(setTrackDetailLoading(true));
                dispatch(setTrackOrderDetail(null));
                // Also reset the per-order derived state — leaving the previous order's
                // leg/distance in redux flashes stale values ("398 miles", old PLI)
                // through the skeleton state until the new route fetch resolves.
                dispatch(setSelectedLeg({ leg: null, index: 0 }));
                dispatch(setFullRouteDistance(0));
                dispatch(setRemainingDistance(0));
            }

            getTrackOrderDetail(id)
                .then((response: any) => {
                    const data = response?.data || null;
                    dispatch(setTrackOrderDetail(data));

                    const legs = data?.legs ?? [];

                    if (legs.length > 0) {
                        // A silent pass is a background refresh (socket live-sync) of the order
                        // the customer is already reading — re-point the pager at the SAME leg
                        // they paged to, by id, instead of snapping back to the active one.
                        // Falls through to the default rule when that leg is gone from the payload.
                        const keptIndex = silent ? legs.findIndex((leg: any) => leg?.legDbId === selectedLegRef.current?.legDbId) : -1;

                        if (keptIndex >= 0) {
                            dispatch(setSelectedLeg({ leg: legs[keptIndex], index: keptIndex }));
                        } else {
                            // Default the leg pager to the leg the freight is physically on —
                            // same rule as the order page's NBA card — instead of always leg 1.
                            const activeLeg = getPhysicallyActiveLeg(legs);
                            const activeIndex = Math.max(
                                0,
                                legs.findIndex((leg: any) => leg?.legDbId === activeLeg?.legDbId)
                            );
                            dispatch(setSelectedLeg({ leg: legs[activeIndex], index: activeIndex }));
                        }
                    }
                })
                .catch((err: any) => {
                    if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
                        // A silent refresh was never requested by the customer — neither the toast
                        // nor the bounce back to the list belongs to a background failure. The page
                        // keeps the last good data and waits for the next event.
                        if (!silent) {
                            WalToast.error(t('Common.Something_went_wrong'));
                            navigate(PATH.TRACK);
                        }
                    }
                })
                .finally(() => {
                    if (!silent) dispatch(setTrackDetailLoading(false));
                });
        },
        [dispatch, navigate, t]
    );

    useEffect(() => {
        const legs = trackOrderDetailData?.legs ?? [];
        if (legs.length === 0) return;

        const firstLeg = legs[0];
        const lastLeg = legs[legs.length - 1];

        const shipperLng = Number(firstLeg?.shipper?.longitude);
        const shipperLat = Number(firstLeg?.shipper?.latitude);
        const consigneeLng = Number(lastLeg?.consignee?.longitude);
        const consigneeLat = Number(lastLeg?.consignee?.latitude);
        const token = (window as any)?.MAPBOX_ACCESSTOKEN;

        if (!shipperLng || !shipperLat || !consigneeLng || !consigneeLat || !token) return;

        const shipperCountry = firstLeg?.shipper?.country;
        const consigneeCountry = lastLeg?.consignee?.country;
        const isSameCountry = Boolean(shipperCountry && consigneeCountry && shipperCountry === consigneeCountry);

        const orderStatus = trackOrderDetailData?.orderStatus;
        const hasLocationUpdates = orderStatus !== ORDER_STATUS.DISPATCHED && orderStatus !== ORDER_STATUS.BOOKED;

        // Newest-first list — the shared fetcher reverses internally so the route runs
        // start → oldest ping → … → newest ping → end (same as WAL radar).
        // STARTED legs only, matching updateRouteAndMarkers in RadarOrderDetail/index.tsx —
        // both route computations must use the same waypoint set or Total and Remaining
        // distance are measured over different roads.
        const allLocationUpdates = hasLocationUpdates ? getStartedLegLocationUpdates(legs) : [];

        const hasOutOfCountryWaypoint = allLocationUpdates.some((u: any) => u?.country && u.country !== shipperCountry);
        const preventCrossBorder = isSameCountry && !hasOutOfCountryWaypoint;

        // Shared segmented fetcher (25-coord chunking + max-distance/NoRoute pairwise
        // splits) — a raw single Directions request 422s on long multi-waypoint routes.
        getDrivingRouteWithWaypoints(
            { lat: shipperLat, lng: shipperLng },
            allLocationUpdates.map((u: any) => ({ lat: Number(u.latitude), lng: Number(u.longitude) })),
            { lat: consigneeLat, lng: consigneeLng },
            preventCrossBorder,
            token
        )
            .then(({ distanceMiles }) => {
                if (distanceMiles) {
                    dispatch(setFullRouteDistance(distanceMiles));
                }
            })
            .catch(() => {
                dispatch(setFullRouteDistance(0));
            });
    }, [trackOrderDetailData, dispatch]);

    const clearOrderDetail = useCallback(() => {
        if (detailAbortRef.current) detailAbortRef.current.abort();
        dispatch(clearTrackOrderDetail());
    }, [dispatch]);

    const handleSelectLeg = useCallback(
        (leg: any, index: number) => {
            dispatch(setSelectedLeg({ leg, index }));
        },
        [dispatch]
    );

    return {
        fetchOrderDetail,
        clearOrderDetail,
        handleSelectLeg,
        trackOrderDetail: trackOrderDetailData,
        isDetailLoading,
        selectedLeg,
        selectedLegIndex,
        fullRouteDistance,
        remainingDistance,
    };
};
